import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import s from "./auswahl.module.css";

interface Props {
  anker: RefObject<HTMLElement | null>;
  offen: boolean;
  onSchliessen: () => void;
  /** Name für Screenreader, zum Beispiel „Frist wählen“ */
  label: string;
  breite?: number | undefined;
  /** Menü statt Fenster (Menü ⋯): role="menu", rechtsbündig unter dem Knopf */
  rolle?: "dialog" | "menu" | undefined;
  /** Bündig mit welcher Kante des Knopfs. Vorgabe: Menü rechts, sonst links. „auto“: rechts, wenn der Knopf in der rechten Hälfte steht. */
  ausrichtung?: "links" | "rechts" | "auto" | undefined;
  children: ReactNode;
}

const HANDY = 640;

/** Schwebendes Fenster unter einem Knopf (Kalender, Personenauswahl). Fest zum Bildschirm positioniert, damit es in scrollenden
 *  Dialogen nicht abgeschnitten wird. Am Handy unten über die ganze Breite. Klick daneben und Escape schließen, Escape gibt den Fokus
 *  an den Knopf zurück. */
export function Schwebend({ anker, offen, onSchliessen, label, breite = 360, rolle = "dialog", ausrichtung, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [stil, setStil] = useState<{ top: number; left: number } | null>(null);
  const [handy, setHandy] = useState(false);

  useLayoutEffect(() => {
    if (!offen) return;
    function setzen() {
      const a = anker.current?.getBoundingClientRect();
      const h = ref.current?.offsetHeight ?? 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (vw < HANDY) {
        setHandy(true);
        setStil(null);
        return;
      }
      setHandy(false);
      if (!a) return;
      const w = Math.min(breite, vw - 32);
      const art = ausrichtung ?? (rolle === "menu" ? "rechts" : "links");
      const rechts = art === "rechts" || (art === "auto" && a.left + a.width / 2 > vw / 2);
      const left = Math.max(16, Math.min(rechts ? a.right - w : a.left, vw - w - 16));
      const unten = a.bottom + 6;
      const top = unten + h > vh - 16 && a.top - h - 6 > 16 ? a.top - h - 6 : Math.max(16, Math.min(unten, vh - h - 16));
      setStil({ top, left });
    }
    setzen();
    window.addEventListener("resize", setzen);
    window.addEventListener("scroll", setzen, true);
    return () => {
      window.removeEventListener("resize", setzen);
      window.removeEventListener("scroll", setzen, true);
    };
  }, [offen, anker, breite, rolle, ausrichtung]);

  useEffect(() => {
    if (!offen) return;
    function klick(e: PointerEvent) {
      const ziel = e.target as Node;
      if (ref.current?.contains(ziel) || anker.current?.contains(ziel)) return;
      onSchliessen();
    }
    document.addEventListener("pointerdown", klick, true);
    return () => document.removeEventListener("pointerdown", klick, true);
  }, [offen, anker, onSchliessen]);

  if (!offen) return null;
  return (
    <div
      ref={ref}
      role={rolle}
      aria-label={label}
      className={[s.schwebend, handy && s.schwebendHandy].filter(Boolean).join(" ")}
      style={handy ? undefined : { top: stil?.top ?? -9999, left: stil?.left ?? -9999, width: Math.min(breite, window.innerWidth - 32) }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          // Nur dieses Fenster schließen, nicht den Dialog darunter
          e.preventDefault();
          e.stopPropagation();
          onSchliessen();
          anker.current?.focus();
        }
      }}
    >
      {children}
    </div>
  );
}
